/**
 * Offline-first outbound messages: write to local DB before server ACK.
 */

import axios from 'axios';
import { API_URL } from '../config/api';
import { getRealmBridge, realmAvailable } from './realmBridge';
import { isRealmEnabled } from './isRealmEnabled';
import {
  attachmentsForQueue,
  resolveAttachmentsForSend,
  toServerAttachments,
} from '../services/offlineAttachmentStore';
import {
  applyOptimisticReaction,
  WORKFLOW_CLOSED_STATUS_ID,
  WORKFLOW_URGENT_ID,
  messageHasUrgentWorkflow,
  getActiveWorkflowByCategory,
  isWorkflowCreatingWorkItemType,
} from '../utils/reactionConfig';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const newLocalId = () => `pending-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const upsert = async (type, docs) => {
  const bridge = getRealmBridge();
  await bridge.upsert(type, docs);
};

const remove = async (type, id) => {
  const bridge = getRealmBridge();
  await bridge.delete(type, id);
};

const queryAll = async (type) => {
  const bridge = getRealmBridge();
  return bridge.query({ type });
};

export const isNetworkFailure = (error) => {
  if (!error) return true;
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') return true;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;
  const status = error?.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  return !error.response;
};

const TASK_FALLBACK_KEY = 'workloop_outbound_tasks';

const readFallbackTasks = () => {
  try {
    const raw = localStorage.getItem(TASK_FALLBACK_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeFallbackTasks = (tasks) => {
  try {
    localStorage.setItem(TASK_FALLBACK_KEY, JSON.stringify(tasks));
  } catch {
    // ignore quota
  }
};

const upsertFallbackTask = (task) => {
  const id = task?.id || task?._id;
  writeFallbackTasks(readFallbackTasks().filter((item) => (item.id || item._id) !== id).concat(task));
};

const removeFallbackTask = (id) => {
  writeFallbackTasks(readFallbackTasks().filter((item) => (item.id || item._id) !== id));
};

const realmReady = async () => isRealmEnabled() && (await realmAvailable());

const isPendingTask = (task) => {
  if (!task) return false;
  const id = String(task.id || task._id || '');
  return task.syncStatus === 'pending' || id.startsWith('pending-');
};

const asIdList = (value) => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.map((id) => String(id || '').trim()).filter(Boolean);
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (typeof value === 'object' && typeof value.forEach === 'function') {
    const ids = [];
    value.forEach((id) => {
      const next = String(id || '').trim();
      if (next) ids.push(next);
    });
    return ids;
  }
  return [];
};

const buildTaskFromPatch = (existing, patch, userId) => {
  const now = new Date().toISOString();
  const merged = { ...(existing || {}), ...(patch || {}) };
  const pctRaw = merged.completionPercentage;
  const pct = pctRaw == null ? (existing?.completionPercentage ?? 0) : Math.max(0, Math.min(100, Math.round(Number(pctRaw) || 0)));
  let status = merged.status || existing?.status || 'PENDING';
  if (typeof status !== 'string') status = String(status);
  let completed = merged.completed ?? existing?.completed ?? null;
  if (pct >= 100 || status === 'COMPLETED') {
    status = 'COMPLETED';
    completed = completed || now;
  }
  const id = existing?.id || existing?._id || newLocalId();
  const outboundOp = String(id).startsWith('pending-') ? 'create' : 'update';
  const previousRequest = existing?.outboundRequest && typeof existing.outboundRequest === 'object'
    ? existing.outboundRequest
    : {};
  const outboundRequest = outboundOp === 'create'
    ? {
        title: merged.title ?? '',
        description: merged.description ?? '',
        messageId: merged.messageId || '',
        workflowMessageIds: Array.isArray(merged.workflowMessageIds) ? merged.workflowMessageIds : [],
        mapTaskId: merged.mapTaskId || '',
        delegateId: asIdList(merged.delegateId).length ? asIdList(merged.delegateId) : asIdList(existing?.delegateId),
        estimateTimeInMinutes: merged.estimateTimeInMinutes,
        completionTimeInMinutes: merged.completionTimeInMinutes,
        completionPercentage: pct,
        status,
        tags: Array.isArray(merged.tags) ? merged.tags : [],
        spaceId: merged.spaceId || '',
        spaceName: merged.spaceName || '',
        ...(patch?.clearUrgentDeadline
          ? { clearUrgentDeadline: true }
          : (merged.urgentDeadlineAt
            ? { urgentDeadlineAt: String(merged.urgentDeadlineAt) }
            : {})),
      }
    : { ...previousRequest, ...patch, completionPercentage: patch?.completionPercentage ?? previousRequest.completionPercentage };

  return {
    ...existing,
    ...merged,
    _id: id,
    id,
    title: merged.title ?? existing?.title ?? '',
    description: merged.description ?? existing?.description ?? '',
    messageId: merged.messageId || existing?.messageId || '',
    workflowMessageIds: Array.isArray(merged.workflowMessageIds)
      ? merged.workflowMessageIds
      : (Array.isArray(existing?.workflowMessageIds) ? existing.workflowMessageIds : []),
    mapTaskId: merged.mapTaskId || existing?.mapTaskId || '',
    createdByUserId: existing?.createdByUserId || userId || null,
    delegateId: asIdList(merged.delegateId).length
      ? asIdList(merged.delegateId)
      : asIdList(existing?.delegateId),
    estimateTimeInMinutes: merged.estimateTimeInMinutes ?? existing?.estimateTimeInMinutes ?? null,
    completionTimeInMinutes: merged.completionTimeInMinutes ?? existing?.completionTimeInMinutes ?? null,
    urgentDeadlineAt: patch?.clearUrgentDeadline
      ? null
      : (merged.urgentDeadlineAt ?? existing?.urgentDeadlineAt ?? null),
    completionPercentage: pct,
    status,
    tags: Array.isArray(merged.tags)
      ? merged.tags
      : (Array.isArray(existing?.tags) ? existing.tags : []),
    spaceId: merged.spaceId || existing?.spaceId || '',
    spaceName: merged.spaceName || existing?.spaceName || '',
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    completed,
    pickedAt: status === 'PICK_TODAY' ? (existing?.pickedAt || now) : (merged.pickedAt ?? existing?.pickedAt ?? null),
    queued: true,
    syncStatus: 'pending',
    outboundOp,
    outboundRequest,
  };
};

const persistLocalTask = async (task) => {
  try {
    if (await realmReady()) {
      await upsert('Task', [task]);
      return task;
    }
  } catch (err) {
    console.warn('[realm] task local write failed', err);
  }
  upsertFallbackTask(task);
  return task;
};

export const writePendingTaskToRealm = async ({ taskData, userId }) => {
  const local = buildTaskFromPatch(null, taskData || {}, userId);
  return persistLocalTask(local);
};

const notifyTasksFlushed = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('workloop:tasks-flushed'));
  }
};

const stripHtmlForTitle = (html) => {
  const value = html == null ? '' : String(html).trim();
  if (!value) return '';
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

const readAllTasks = async () => {
  if (await realmReady()) {
    return queryAll('Task');
  }
  return readFallbackTasks();
};

const isTitledJourneyTask = (task) => Boolean(
  task
  && !(task.mapTaskId || '').trim()
  && (task.title || '').trim()
  && task.outboundOp !== 'delete',
);

const fetchServerJourneyTasksForMessage = async (messageId) => {
  const response = await axios.get(`${API_URL}/tasks`, {
    headers: authHeaders(),
    params: { messageId },
    timeout: 15000,
  });
  const items = Array.isArray(response.data) ? response.data : [];
  return items.filter(isTitledJourneyTask);
};

/**
 * After a workflow is raised (online reaction or flush), align any pending local
 * journey task with the server copy the API created. Avoids duplicate POST on flush.
 */
export const reconcileJourneyTasksForMessage = async (messageId) => {
  if (!messageId) return null;

  let serverTask = null;
  try {
    const serverJourney = await fetchServerJourneyTasksForMessage(messageId);
    serverTask = serverJourney[0] || null;
  } catch {
    return null;
  }

  const all = await readAllTasks();
  const pendingLocals = all.filter((task) => {
    const id = String(task.id || task._id || '');
    const mid = String(task.messageId || '');
    return mid === String(messageId)
      && (id.startsWith('pending-') || task.syncStatus === 'pending');
  });

  if (!serverTask) {
    return pendingLocals[0] || null;
  }

  for (const local of pendingLocals) {
    await markTaskSynced(local.id || local._id, serverTask);
  }
  if (pendingLocals.length > 0) {
    notifyTasksFlushed();
  }
  return serverTask;
};

/**
 * When a chat message is first raised as a workflow while offline, create the
 * linked PICK_TODAY journey task locally (same shape as server addReaction).
 */
export const ensurePendingJourneyTaskForWorkflow = async ({
  messageId,
  message,
  workflowTitle,
  workflowDescription,
  userId,
}) => {
  if (!messageId) return null;

  const all = await readAllTasks();
  const linked = all.filter((task) => {
    if ((task.mapTaskId || '').trim()) return false;
    return String(task.messageId || '') === String(messageId);
  });
  if (linked.some(isTitledJourneyTask)) {
    return null;
  }

  let title = typeof workflowTitle === 'string' ? workflowTitle.trim() : '';
  if (!title) title = (message?.workflowTitle || '').trim();
  if (!title) {
    title = stripHtmlForTitle(message?.workflowDescription || message?.content || '');
  }
  if (!title) title = 'Workflow';
  title = title.slice(0, 200);

  let description = workflowDescription != null ? String(workflowDescription) : '';
  if (!description.trim()) {
    description = message?.workflowDescription || message?.content || '';
  }

  const assignee = (message?.workflowAssignedToUserId || '').trim();
  const delegateId = assignee && userId && assignee !== userId ? [assignee] : [];

  const local = await writePendingTaskToRealm({
    taskData: {
      title,
      description,
      messageId,
      status: 'PICK_TODAY',
      delegateId,
      workflowMessageIds: [],
      mapTaskId: '',
      spaceId: message?.workflowSpaceId || '',
      spaceName: message?.workflowSpaceName || '',
    },
    userId,
  });
  if (local?.outboundRequest) {
    local.outboundRequest.journeyFromWorkflowRaise = true;
    await persistLocalTask(local);
  }
  notifyTasksFlushed();
  return local;
};

export const applyPendingTaskUpdate = async ({ taskId, patch, existing, userId }) => {
  let current = existing;
  if (!current && (await realmReady())) {
    const all = await queryAll('Task');
    current = all.find((task) => (task.id || task._id) === taskId) || null;
  }
  if (!current) {
    current = readFallbackTasks().find((task) => (task.id || task._id) === taskId) || { id: taskId, _id: taskId };
  }
  const local = buildTaskFromPatch(current, patch || {}, userId || current.createdByUserId);
  local.id = taskId;
  local._id = taskId;
  if (!String(taskId).startsWith('pending-')) {
    local.outboundOp = 'update';
    local.outboundRequest = { ...(current.outboundRequest || {}), ...(patch || {}) };
  }
  return persistLocalTask(local);
};

export const markTaskSynced = async (localId, serverTask) => {
  if (await realmReady()) {
    if (localId && String(localId).startsWith('pending-')) {
      await remove('Task', localId);
    }
    if (serverTask) {
      await upsert('Task', [{ ...serverTask, syncStatus: 'synced', queued: false, outboundRequest: null, outboundOp: null }]);
    }
  }
  removeFallbackTask(localId);
  if (serverTask?.id && serverTask.id !== localId) {
    removeFallbackTask(serverTask.id);
  }
};

export const listPendingTasks = async () => {
  const fromRealm = (await realmReady())
    ? (await queryAll('Task')).filter(isPendingTask)
    : [];
  const fromFallback = readFallbackTasks().filter(isPendingTask);
  const byId = new Map();
  [...fromRealm, ...fromFallback].forEach((task) => {
    byId.set(task.id || task._id, task);
  });
  return [...byId.values()];
};

export const overlayPendingTasks = async (serverList = []) => {
  const pending = await listPendingTasks();
  if (!pending.length) return Array.isArray(serverList) ? serverList : [];
  const deletedIds = new Set(
    pending
      .filter((task) => task.outboundOp === 'delete')
      .map((task) => task.id || task._id),
  );
  const pendingById = new Map(pending.map((task) => [task.id || task._id, task]));
  const used = new Set();
  const merged = (Array.isArray(serverList) ? serverList : [])
    .filter((task) => !deletedIds.has(task.id) && !deletedIds.has(task._id))
    .map((task) => {
      const pendingTask = pendingById.get(task.id) || pendingById.get(task._id);
      if (!pendingTask || pendingTask.outboundOp === 'delete') return task;
      used.add(pendingTask.id || pendingTask._id);
      return { ...task, ...pendingTask, id: task.id || pendingTask.id };
    });
  pending.forEach((task) => {
    const id = task.id || task._id;
    if (task.outboundOp === 'delete' || used.has(id)) return;
    merged.push(task);
  });
  return merged;
};

const sameUserId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

export const queuePendingTaskDelete = async ({ taskId, existing, userId }) => {
  let current = existing;
  if (!current && (await realmReady())) {
    const all = await queryAll('Task');
    current = all.find((task) => (task.id || task._id) === taskId) || null;
  }
  if (!current) {
    current = readFallbackTasks().find((task) => (task.id || task._id) === taskId) || null;
  }
  const isMappedChat = Boolean(current?.mapTaskId && current?.messageId);
  if (!isMappedChat && current?.createdByUserId && userId && !sameUserId(current.createdByUserId, userId)) {
    throw new Error('You don\'t have permission to delete this task. Only the task creator can delete tasks.');
  }
  if (!current) {
    current = { id: taskId, _id: taskId };
  }
  const local = {
    ...current,
    id: taskId,
    _id: taskId,
    queued: true,
    syncStatus: 'pending',
    outboundOp: 'delete',
    outboundRequest: { id: taskId },
    updatedAt: new Date().toISOString(),
  };
  return persistLocalTask(local);
};

/**
 * Retry pending task creates/updates when back online.
 */
export const flushOutboundTaskQueue = async () => {
  const pending = await listPendingTasks();
  let flushed = 0;
  const synced = [];
  for (const task of pending) {
    const localId = task.id || task._id;
    const op = task.outboundOp || (String(localId).startsWith('pending-') ? 'create' : 'update');
    const body = task.outboundRequest || {};
    try {
      let response;
      if (op === 'delete') {
        await axios.delete(`${API_URL}/tasks/${localId}`, {
          headers: authHeaders(),
          timeout: 15000,
        });
        await markTaskSynced(localId, null);
        flushed += 1;
        synced.push({ localId, serverTask: null, deleted: true });
        continue;
      }
      if (op === 'create') {
        if (body.messageId && (body.title || '').trim() && !(body.mapTaskId || '').trim()) {
          const reconciled = await reconcileJourneyTasksForMessage(body.messageId);
          const serverId = reconciled?.id || reconciled?._id;
          if (serverId && !String(serverId).startsWith('pending-')) {
            await markTaskSynced(localId, reconciled);
            flushed += 1;
            synced.push({ localId, serverTask: reconciled });
            continue;
          }
        }
        response = await axios.post(`${API_URL}/tasks`, body, {
          headers: authHeaders(),
          timeout: 15000,
        });
      } else {
        response = await axios.put(`${API_URL}/tasks/${localId}`, body, {
          headers: authHeaders(),
          timeout: 15000,
        });
      }
      await markTaskSynced(localId, response.data);
      flushed += 1;
      synced.push({ localId, serverTask: { ...response.data, syncStatus: 'synced' } });
    } catch (err) {
      if (!isNetworkFailure(err) && (await realmReady())) {
        await upsert('Task', [{ ...task, syncStatus: 'failed' }]);
      }
    }
  }
  if (typeof window !== 'undefined' && flushed > 0) {
    window.dispatchEvent(new CustomEvent('workloop:tasks-flushed'));
  }
  return { flushed, pending: pending.length - flushed, synced };
};

const REMINDER_FALLBACK_KEY = 'workloop_outbound_reminders';

const readFallbackReminders = () => {
  try {
    const raw = localStorage.getItem(REMINDER_FALLBACK_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeFallbackReminders = (reminders) => {
  try {
    localStorage.setItem(REMINDER_FALLBACK_KEY, JSON.stringify(reminders));
  } catch {
    // ignore quota
  }
};

const upsertFallbackReminder = (reminder) => {
  const id = reminder?.id || reminder?._id;
  writeFallbackReminders(readFallbackReminders().filter((item) => (item.id || item._id) !== id).concat(reminder));
};

const removeFallbackReminder = (id) => {
  writeFallbackReminders(readFallbackReminders().filter((item) => (item.id || item._id) !== id));
};

const isPendingReminder = (reminder) => {
  if (!reminder) return false;
  const id = String(reminder.id || reminder._id || '');
  return reminder.syncStatus === 'pending' || id.startsWith('pending-');
};

const persistLocalReminder = async (reminder) => {
  try {
    if (await realmReady()) {
      await upsert('Reminder', [reminder]);
      return reminder;
    }
  } catch (err) {
    console.warn('[realm] reminder local write failed', err);
  }
  upsertFallbackReminder(reminder);
  return reminder;
};

export const writePendingReminderToRealm = async ({ data, userId }) => {
  const now = new Date().toISOString();
  const id = newLocalId();
  const local = {
    ...(data || {}),
    _id: id,
    id,
    userId: userId || data?.userId || null,
    completedDates: Array.isArray(data?.completedDates) ? data.completedDates : [],
    skippedDates: Array.isArray(data?.skippedDates) ? data.skippedDates : [],
    createdAt: now,
    updatedAt: now,
    queued: true,
    syncStatus: 'pending',
    outboundOp: 'create',
    outboundRequest: data || {},
  };
  return persistLocalReminder(local);
};

export const applyPendingReminderUpdate = async ({ reminderId, patch, existing, outboundOp = 'update', outboundRequest }) => {
  let current = existing && (existing.id || existing._id) ? existing : null;
  if (!current && (await realmReady())) {
    const all = await queryAll('Reminder');
    current = all.find((item) => (item.id || item._id) === reminderId) || null;
  }
  if (!current) {
    current = readFallbackReminders().find((item) => (item.id || item._id) === reminderId) || { id: reminderId, _id: reminderId };
  }
  const now = new Date().toISOString();
  const merged = { ...current, ...(patch || {}) };
  const local = {
    ...merged,
    id: reminderId,
    _id: reminderId,
    updatedAt: now,
    queued: true,
    syncStatus: 'pending',
    outboundOp,
    outboundRequest: outboundOp === 'complete-today' || outboundOp === 'skip-today' || outboundOp === 'toggle-task'
      ? { ...(outboundRequest || {}) }
      : { ...(current.outboundRequest || {}), ...(patch || {}) },
  };
  return persistLocalReminder(local);
};

export const queuePendingReminderDelete = async (reminder) => {
  const reminderId = reminder?.id || reminder?._id;
  if (!reminderId) return null;
  return applyPendingReminderUpdate({
    reminderId,
    existing: reminder,
    outboundOp: 'delete',
    patch: {},
  });
};

export const markReminderSynced = async (localId, serverReminder) => {
  if (await realmReady()) {
    if (localId && (String(localId).startsWith('pending-') || !serverReminder)) {
      await remove('Reminder', localId);
    }
    if (serverReminder) {
      await upsert('Reminder', [{
        ...serverReminder,
        syncStatus: 'synced',
        queued: false,
        outboundRequest: null,
        outboundOp: null,
      }]);
    }
  }
  removeFallbackReminder(localId);
  if (serverReminder?.id && serverReminder.id !== localId) {
    removeFallbackReminder(serverReminder.id);
  }
};

export const listPendingReminders = async () => {
  const fromRealm = (await realmReady())
    ? (await queryAll('Reminder')).filter(isPendingReminder)
    : [];
  const fromFallback = readFallbackReminders().filter(isPendingReminder);
  const byId = new Map();
  [...fromRealm, ...fromFallback].forEach((reminder) => {
    byId.set(reminder.id || reminder._id, reminder);
  });
  return [...byId.values()];
};

export const overlayPendingReminders = async (serverList = []) => {
  const pending = await listPendingReminders();
  if (!pending.length) return Array.isArray(serverList) ? serverList : [];
  const deletedIds = new Set(
    pending
      .filter((reminder) => reminder.outboundOp === 'delete')
      .map((reminder) => reminder.id || reminder._id),
  );
  const pendingById = new Map(pending.map((reminder) => [reminder.id || reminder._id, reminder]));
  const used = new Set();
  const merged = (Array.isArray(serverList) ? serverList : [])
    .filter((reminder) => !deletedIds.has(reminder.id) && !deletedIds.has(reminder._id))
    .map((reminder) => {
      const pendingReminder = pendingById.get(reminder.id) || pendingById.get(reminder._id);
      if (!pendingReminder || pendingReminder.outboundOp === 'delete') return reminder;
      used.add(pendingReminder.id || pendingReminder._id);
      return { ...reminder, ...pendingReminder, id: reminder.id || pendingReminder.id };
    });
  pending.forEach((reminder) => {
    const id = reminder.id || reminder._id;
    if (reminder.outboundOp === 'delete' || used.has(id)) return;
    merged.push(reminder);
  });
  return merged;
};

export const flushOutboundReminderQueue = async () => {
  const pending = await listPendingReminders();
  let flushed = 0;
  const synced = [];
  for (const reminder of pending) {
    const localId = reminder.id || reminder._id;
    const op = reminder.outboundOp || (String(localId).startsWith('pending-') ? 'create' : 'update');
    const body = reminder.outboundRequest || {};
    try {
      let response;
      if (op === 'delete') {
        await axios.delete(`${API_URL}/reminders/${localId}`, {
          headers: authHeaders(),
          timeout: 15000,
        });
        await markReminderSynced(localId, null);
        flushed += 1;
        synced.push({ localId, serverReminder: null, deleted: true });
        continue;
      }
      if (op === 'complete-today') {
        response = await axios.post(`${API_URL}/reminders/${localId}/complete-today`, {}, {
          headers: authHeaders(),
          timeout: 15000,
          params: body?.date ? { date: body.date } : {},
        });
      } else if (op === 'toggle-task') {
        response = await axios.post(`${API_URL}/reminders/${localId}/tasks/${encodeURIComponent(body.taskId)}/toggle`, {}, {
          headers: authHeaders(),
          timeout: 15000,
          params: body?.date ? { date: body.date } : {},
        });
      } else if (op === 'skip-today') {
        response = await axios.post(`${API_URL}/reminders/${encodeURIComponent(localId)}/skip-today`, {}, {
          headers: authHeaders(),
          timeout: 15000,
          params: body?.date ? { date: body.date } : {},
        });
      } else if (op === 'create') {
        response = await axios.post(`${API_URL}/reminders`, body, {
          headers: authHeaders(),
          timeout: 15000,
        });
      } else {
        response = await axios.put(`${API_URL}/reminders/${localId}`, body, {
          headers: authHeaders(),
          timeout: 15000,
        });
      }
      await markReminderSynced(localId, response.data);
      flushed += 1;
      synced.push({ localId, serverReminder: { ...response.data, syncStatus: 'synced' } });
    } catch (err) {
      if (!isNetworkFailure(err) && (await realmReady())) {
        await upsert('Reminder', [{ ...reminder, syncStatus: 'failed' }]);
      }
    }
  }
  if (typeof window !== 'undefined' && flushed > 0) {
    window.dispatchEvent(new CustomEvent('workloop:reminders-flushed'));
  }
  return { flushed, pending: pending.length - flushed, synced };
};

export const queuePendingReaction = async ({
  message,
  emoji,
  completionTimeInMinutes,
  urgentDeadlineAt,
  workflowTitle,
  workflowDescription,
  workflowSpaceId,
  workflowSpaceName,
  userId,
}) => {
  const messageId = message?.id || message?._id;
  if (!messageId || !emoji) return null;
  let existing = message || {};
  try {
    if (await realmReady()) {
      const all = await queryAll('Message');
      const fromDb = all.find((row) => String(row.id || row._id) === String(messageId));
      if (fromDb) existing = { ...fromDb, ...existing };
    }
  } catch {
    // Keep the in-memory message.
  }
  const minutes = Number(completionTimeInMinutes);
  const title = typeof workflowTitle === 'string' ? workflowTitle.trim() : '';
  const descriptionProvided = workflowDescription !== undefined && workflowDescription !== null;
  const hadWorkItemType = Boolean(getActiveWorkflowByCategory(existing.reactions || [])['work-item-type']);
  const creatingWorkItemType = isWorkflowCreatingWorkItemType(emoji) && !hadWorkItemType;
  const wasUrgent = messageHasUrgentWorkflow(existing.reactions);
  const clearingUrgent = (emoji === WORKFLOW_URGENT_ID || String(emoji).startsWith('wf:urgency:'))
    && wasUrgent;
  const nextDeadline = clearingUrgent
    ? null
    : (urgentDeadlineAt ? String(urgentDeadlineAt) : (existing.urgentDeadlineAt || null));
  const nextReactions = applyOptimisticReaction(existing.reactions, emoji, userId);
  const next = {
    ...existing,
    _id: messageId,
    id: messageId,
    channelId: existing.channelId || message?.channelId || null,
    reactions: nextReactions,
    completionTimeInMinutes: Number.isFinite(minutes) && minutes > 0
      ? minutes
      : existing.completionTimeInMinutes,
    urgentDeadlineAt: nextDeadline,
    ...(title ? { workflowTitle: title.slice(0, 200) } : {}),
    ...(descriptionProvided ? { workflowDescription: String(workflowDescription) } : {}),
    ...(workflowSpaceId ? { workflowSpaceId: String(workflowSpaceId) } : {}),
    ...(workflowSpaceName ? { workflowSpaceName: String(workflowSpaceName) } : {}),
    workflowByCategory: getActiveWorkflowByCategory(nextReactions),
    updatedAt: new Date().toISOString(),
    syncStatus: 'pending',
    outboundOp: 'reaction',
    outboundRequest: {
      emoji,
      completionTimeInMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : undefined,
      ...(clearingUrgent
        ? {}
        : (nextDeadline ? { urgentDeadlineAt: String(nextDeadline) } : {})),
      ...(title ? { workflowTitle: title.slice(0, 200) } : {}),
      ...(descriptionProvided ? { workflowDescription: String(workflowDescription) } : {}),
      ...(workflowSpaceId ? { workflowSpaceId: String(workflowSpaceId) } : {}),
      ...(workflowSpaceName ? { workflowSpaceName: String(workflowSpaceName) } : {}),
    },
  };
  // Drop undefined urgency key when clearing
  if (clearingUrgent && next.workflowByCategory) {
    const { urgency: _drop, ...restCats } = next.workflowByCategory;
    next.workflowByCategory = restCats;
  }
  if (await realmReady()) {
    await upsert('Message', [next]);
  }
  if (creatingWorkItemType) {
    try {
      await ensurePendingJourneyTaskForWorkflow({
        messageId,
        message: next,
        workflowTitle: title,
        workflowDescription: descriptionProvided ? workflowDescription : undefined,
        userId,
      });
    } catch (err) {
      console.warn('[realm] journey task for workflow raise failed', err?.message || err);
    }
  }
  return next;
};

export const rollbackQueuedReaction = async (originalMessage) => {
  const messageId = originalMessage?.id || originalMessage?._id;
  if (!messageId) return;
  const restored = {
    ...originalMessage,
    _id: messageId,
    id: messageId,
    syncStatus: originalMessage.syncStatus === 'pending' ? 'synced' : (originalMessage.syncStatus || 'synced'),
    outboundOp: originalMessage.outboundOp === 'reaction' ? null : originalMessage.outboundOp,
    outboundRequest: originalMessage.outboundOp === 'reaction' ? null : originalMessage.outboundRequest,
  };
  if (await realmReady()) {
    await upsert('Message', [restored]);
  }
};

/**
 * Persist a pending message locally (Electron Realm). Returns local row id.
 */
export const writePendingMessageToRealm = async ({
  channelId,
  content,
  senderId,
  replyToMessageId,
  mentionedUserIds,
  attachments,
  linkPreview,
  attachmentGroupName,
  targetResourceGroupId,
  tempId,
  workItemShare,
  sensitive,
  senderPin,
  allowReceiverPin,
  sensitiveTitle,
}) => {
  if (!isRealmEnabled() || !(await realmAvailable())) {
    return null;
  }
  const localId = (tempId && String(tempId).trim()) || newLocalId();
  const now = new Date().toISOString();
  const persistableAttachments = attachmentsForQueue(attachments);
  const outboundRequest = {
    content: content || '',
    channelId,
    replyToMessageId,
    mentionedUserIds,
    attachments: persistableAttachments,
    linkPreview,
    attachmentGroupName,
    targetResourceGroupId,
    workItemShare,
    clientMessageId: tempId || localId,
  };
  if (sensitive) {
    outboundRequest.sensitive = true;
    outboundRequest.allowReceiverPin = allowReceiverPin === true;
    if (sensitiveTitle) outboundRequest.sensitiveTitle = sensitiveTitle;
    if (senderPin) outboundRequest.senderPin = senderPin;
  }
  const localMessage = {
    _id: localId,
    id: localId,
    channelId,
    content: sensitive ? '' : (content || ''),
    sensitive: Boolean(sensitive),
    sensitiveTitle: sensitive ? (sensitiveTitle || '') : undefined,
    allowReceiverPin: sensitive ? allowReceiverPin === true : undefined,
    hasSenderPin: Boolean(sensitive && senderPin),
    senderId,
    replyToMessageId: replyToMessageId || null,
    createdAt: now,
    updatedAt: now,
    syncStatus: 'pending',
    tempId: tempId || null,
    attachments: persistableAttachments,
    outboundRequest,
    workItemShare: workItemShare || null,
  };
  await upsert('Message', [localMessage]);
  return localMessage;
};

export const markMessageSynced = async (localId, serverMessage) => {
  if (localId && (String(localId).startsWith('pending-') || String(localId).startsWith('temp-'))) {
    await remove('Message', localId);
  }
  if (serverMessage) {
    await upsert('Message', [{
      ...serverMessage,
      syncStatus: 'synced',
      outboundOp: null,
      outboundRequest: null,
    }]);
  }
};

/**
 * Retry all pending local messages when back online.
 */
export const flushOutboundMessageQueue = async () => {
  if (!isRealmEnabled() || !(await realmAvailable())) {
    return { flushed: 0, pending: 0, synced: [] };
  }
  const all = await queryAll('Message');
  const pending = all.filter((m) => {
    const id = String(m.id || m._id || '');
    const pendingCreate = id.startsWith('pending-');
    const pendingStatus = m.syncStatus === 'pending';
    const pendingReaction = m.outboundOp === 'reaction' && pendingStatus;
    return pendingStatus || pendingReaction || pendingCreate;
  });
  let flushed = 0;
  const synced = [];
  for (const msg of pending) {
    const localId = msg.id || msg._id;
    let body = msg.outboundRequest;
    if (!body && msg.payloadJson) {
      try {
        body = JSON.parse(msg.payloadJson).outboundRequest;
      } catch {
        body = null;
      }
    }
    try {
      if (msg.outboundOp === 'reaction') {
        const emoji = body?.emoji;
        if (!emoji) continue;
        const reactionBody = { emoji };
        const minutes = Number(body?.completionTimeInMinutes);
        if (Number.isFinite(minutes) && minutes > 0) {
          reactionBody.completionTimeInMinutes = minutes;
        }
        if (body?.urgentDeadlineAt) {
          reactionBody.urgentDeadlineAt = String(body.urgentDeadlineAt);
        }
        if (typeof body?.workflowTitle === 'string' && body.workflowTitle.trim()) {
          reactionBody.workflowTitle = body.workflowTitle.trim().slice(0, 200);
        }
        if (body?.workflowDescription != null) {
          reactionBody.workflowDescription = String(body.workflowDescription);
        }
        if (typeof body?.workflowSpaceId === 'string' && body.workflowSpaceId.trim()) {
          reactionBody.workflowSpaceId = body.workflowSpaceId.trim();
        }
        if (typeof body?.workflowSpaceName === 'string' && body.workflowSpaceName.trim()) {
          reactionBody.workflowSpaceName = body.workflowSpaceName.trim();
        }
        const response = await axios.post(
          `${API_URL}/messages/${localId}/reaction`,
          reactionBody,
          { headers: authHeaders(), timeout: 15000 },
        );
        await markMessageSynced(localId, response.data);
        if (isWorkflowCreatingWorkItemType(emoji)) {
          try {
            await reconcileJourneyTasksForMessage(localId);
          } catch (reconcileErr) {
            console.warn('[realm] journey task reconcile failed', reconcileErr?.message || reconcileErr);
          }
        }
        flushed += 1;
        synced.push({
          channelId: response.data?.channelId || msg.channelId,
          localId,
          tempId: null,
          serverMessage: { ...response.data, syncStatus: 'synced' },
        });
        continue;
      }
      if (!body?.channelId) continue;
      if (body.attachments?.length) {
        const resolved = await resolveAttachmentsForSend(body.attachments);
        body = {
          ...body,
          attachments: toServerAttachments(resolved),
        };
        await upsert('Message', [{
          ...msg,
          outboundRequest: body,
          attachments: body.attachments,
        }]);
      }
      const response = await axios.post(`${API_URL}/messages`, body, {
        headers: authHeaders(),
        timeout: 15000,
      });
      await markMessageSynced(localId, response.data);
      flushed += 1;
      synced.push({
        channelId: response.data?.channelId || body.channelId,
        localId,
        tempId: msg.tempId || null,
        serverMessage: { ...response.data, syncStatus: 'synced' },
      });
    } catch (err) {
      if (!isNetworkFailure(err)) {
        await upsert('Message', [{ ...msg, syncStatus: 'failed' }]);
      }
    }
  }
  return { flushed, pending: pending.length - flushed, synced };
};
